import axios from 'axios';
import api, { reinitializeApi, setLoggingOut } from './api';
import { API_BASE_URL } from '../config';

const PORTAL_PREFIX = '/portal';

export function normalizeRedirectPath(path) {
  if (!path) {
    return null;
  }

  let workingPath = path.trim();

  try {
    workingPath = decodeURIComponent(workingPath);
  } catch (err) {
    // Ignore decoding issues
  }

  if (/^https?:\/\//i.test(workingPath)) {
    try {
      const url = new URL(workingPath);
      workingPath = `${url.pathname}${url.search}${url.hash}`;
    } catch (err) {
      // Ignore URL parsing issues
    }
  }

  if (!workingPath.startsWith('/')) {
    workingPath = `/${workingPath}`;
  }

  if (workingPath === PORTAL_PREFIX) {
    workingPath = '/';
  } else if (workingPath.startsWith(`${PORTAL_PREFIX}/`)) {
    workingPath = workingPath.substring(PORTAL_PREFIX.length);
    if (!workingPath.startsWith('/')) {
      workingPath = `/${workingPath}`;
    }
  }

  return workingPath || '/';
}

/**
 * Get and clear the stored redirect path after login
 * @returns {string|null} The path to redirect to, or null if none stored
 */
export function getAndClearRedirectPath() {
  const redirectPath = localStorage.getItem('redirectAfterLogin');
  if (redirectPath) {
    localStorage.removeItem('redirectAfterLogin');
    const normalizedPath = normalizeRedirectPath(redirectPath);
    console.log('🔄 Found stored redirect path:', normalizedPath);
    return normalizedPath;
  }
  return null;
}

export function getCurrentUser() {
  try {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error("Failed to parse user data from localStorage", error);
    return null;
  }
}

export async function loginWithMicrosoft() {
  console.log('🔐 Login with Microsoft called');
  // Redirect to the PHP backend for Microsoft login using the correct endpoint
  // Use API_BASE_URL to respect the current environment (localhost, quality, or production)
  const reactAppUrl = window.location.origin;
  window.location.href = `${API_BASE_URL}/login/azure?devOrigin=${encodeURIComponent(reactAppUrl)}`;
}

export async function loginWithUsername(email, password) {
  const response = await api.post('/api/auth_login', { email, password }, {
    headers: {
      'X-App-Type': 'web',
      'X-App-Version': '1.0.0',
      'X-Platform': 'Web Browser'
    }
  });
  
  // Handle different response types
  if (response.data.status === 1) {
    // Traditional complete login (for Microsoft auth, etc.)
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    reinitializeApi();
    
    // Check for stored redirect path
    const redirectPath = getAndClearRedirectPath();
    if (redirectPath) {
      console.log('🔄 Redirecting to stored path:', redirectPath);
      response.data.redirectPath = redirectPath;
    }
    
    return response.data;
  } else if (response.data.status === 'otp_required') {
    // OTP verification required
    return response.data;
  } else {
    // Login failed
    throw new Error(response.data.message || 'Login failed');
  }
}

export async function verifyOTP(otp, otpToken) {
  const response = await api.post('/api/verify_otp', { otp, otp_token: otpToken });
  if (response.data.status === 1 && response.data.token) {
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    reinitializeApi();
    
    // Check for stored redirect path
    const redirectPath = getAndClearRedirectPath();
    if (redirectPath) {
      console.log('🔄 Redirecting to stored path after OTP verification:', redirectPath);
      response.data.redirectPath = redirectPath;
    }
  }
  return response.data;
}

export async function requestPasswordReset(email) {
  const response = await api.post('/api/request_password_reset', { email });
  return response.data;
}

export async function verifyPasswordResetOTP(resetToken, otp) {
  const response = await api.post('/api/verify_password_reset_otp', { 
    reset_token: resetToken, 
    otp 
  });
  return response.data;
}

export async function resetPassword(passwordChangeToken, password) {
  const response = await api.post('/api/reset_password', { 
    password_change_token: passwordChangeToken, 
    password 
  });
  return response.data;
}

export async function resendOTP(otpToken) {
  const response = await api.post('/api/resend_otp', { otp_token: otpToken });
  return response.data;
}

export async function loginWithApprovalToken(approvalToken, ticketId) {
  try {
    console.log('🔐 loginWithApprovalToken: Starting authentication', { ticketId });
    
    // Use the api instance which has proper CORS and base URL handling
    // Temporarily remove token from localStorage to prevent Authorization header
    const existingToken = localStorage.getItem('token');
    if (existingToken) {
      localStorage.removeItem('token');
    }
    
    console.log('🔐 loginWithApprovalToken: Making API call to /api/auth_approval_token');
    
    // Use the configured api instance which handles CORS properly
    const authResponse = await api.post('/api/auth_approval_token', { 
      approval_token: approvalToken,
      ticket_id: ticketId 
    });
    
    console.log('🔐 loginWithApprovalToken: Response received', authResponse.data);
    
    if (authResponse.data.status === 1 && authResponse.data.token) {
      // Store token and user data
      localStorage.setItem('token', authResponse.data.token);
      localStorage.setItem('user', JSON.stringify(authResponse.data.user));
      reinitializeApi();
      
      console.log('✅ loginWithApprovalToken: Authentication successful');
      
      // Check for stored redirect path
      const redirectPath = getAndClearRedirectPath();
      
      return {
        status: 1,
        user: authResponse.data.user,
        token: authResponse.data.token,
        redirectPath: redirectPath // Include redirect path if exists
      };
    } else {
      // Restore existing token if authentication failed
      if (existingToken) {
        localStorage.setItem('token', existingToken);
      }
      throw new Error(authResponse.data.message || 'Failed to authenticate with approval token');
    }
  } catch (error) {
    console.error('❌ Approval token login failed:', error);
    if (error.response) {
      // Server responded with error
      console.error('Server error response:', error.response.data);
      throw new Error(error.response.data.message || 'Authentication failed');
    } else if (error.request) {
      // Request made but no response
      console.error('No response received:', error.request);
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Something else happened
      console.error('Error setting up request:', error.message);
      throw error;
    }
  }
}

export function handleAuthCallback() {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const userDataStr = urlParams.get('user');

  if (token && userDataStr) {
    try {
      const user = JSON.parse(decodeURIComponent(userDataStr));
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      reinitializeApi();
      
      // Check for stored redirect path
      const redirectPath = getAndClearRedirectPath();
      
      // Clean up URL
      window.history.replaceState({}, document.title, "/");

      return { 
        user, 
        token,
        redirectPath: redirectPath // Include redirect path if exists
      };
    } catch (error) {
      console.error('Error handling auth callback:', error);
      return null;
    }
  }
  return null;
}

export async function verifySession() {
  try {
    const response = await api.get('/api/check_session');
    if (response.data.status === 1) {
      // Return the full response data which may include masquerading info
      return response.data;
    }
    return null;
  } catch (error) {
    // It's normal for this to fail if the user is not logged in, so we don't need to log every time.
    if (error.response?.status !== 401) {
      console.error("Session verification failed:", error);
    }
    return null;
  }
}

export async function logout() {
  console.log('🚪 Logout called');
  setLoggingOut(true);
  try {
    // Store current path before logout (so we can redirect back after re-authentication)
    const rawPath = window.location.pathname + window.location.search;
    const normalizedPath = normalizeRedirectPath(rawPath);
    // Don't store the login page itself or auth callback pages
    if (
      normalizedPath &&
      normalizedPath !== '/login' &&
      !normalizedPath.includes('/auth-callback') &&
      !normalizedPath.includes('/auth/callback')
    ) {
      localStorage.setItem('redirectAfterLogin', normalizedPath);
      console.log('💾 Stored path for redirect after re-authentication:', normalizedPath);
    }
    
    await api.post('/api/logout');
  } catch (error) {
    console.error("Logout failed, but proceeding with client-side cleanup.", error);
  } finally {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('masquerading'); // Clear masquerading state on logout
    // Only redirect if we're not already on the login page AND not on MMS mobile routes
    if (window.location.pathname !== '/login' && !window.location.pathname.startsWith('/mms-mobile')) {
      window.location.href = '/login';
    }
  }
}

// Masquerading functions - localStorage-based (simple and consistent)
export async function startMasquerading(targetUserId) {
  try {
    // Get current user from localStorage as original user
    const originalUser = getCurrentUser();
    if (!originalUser) {
      throw new Error('No current user found');
    }
    
    console.log('Starting masquerading for target user ID:', targetUserId);
    
    // Get target user data from API
    const response = await api.get(`/api/users/${targetUserId}`);
    console.log('Target user API response:', response.data);
    
    if (response.data.status === 1) {
      let targetUserData = response.data.data;
      
      // Handle different response structures - sometimes data is nested in 'row'
      if (targetUserData && targetUserData.row) {
        targetUserData = targetUserData.row;
      }
      
      console.log('Raw target user data:', targetUserData);
      
      // Use the complete target user data - don't filter out important fields like role_name, permissions, approver, etc.
      const targetUser = targetUserData;
      
      console.log('Complete target user data for masquerading:', targetUser);
      
      // Store masquerading state in localStorage
      const masqueradeState = {
        isMasquerading: true,
        originalUser: originalUser,
        targetUser: targetUser
      };
      
      localStorage.setItem('masquerading', JSON.stringify(masqueradeState));
      
      // Update localStorage user to target user (for immediate frontend updates)
      localStorage.setItem('user', JSON.stringify(targetUser));
      
      console.log('✅ Masquerading started successfully (localStorage):', targetUser.user_full_name);
      
      return {
        status: 1,
        message: 'Masquerading started successfully',
        originalUser: originalUser,
        targetUser: targetUser
      };
    } else {
      throw new Error(response.data.message || 'Failed to get target user data');
    }
  } catch (error) {
    console.error('Start masquerading failed:', error);
    
    // If the API call fails, let's try to get user data from the users list
    try {
      console.log('Trying to get user from users list...');
      const usersResponse = await api.get('/api/users');
      console.log('Users list response:', usersResponse.data);
      
      if (usersResponse.data.status === 1) {
        const users = usersResponse.data.data || [];
        const foundUser = users.find(u => u.user_id == targetUserId);
        
        if (foundUser) {
          console.log('Found target user in list:', foundUser);
          
          // Use the complete target user data - don't filter out important fields
          const targetUser = foundUser;
          
          console.log('Complete target user data from list:', targetUser);
          
          const originalUser = getCurrentUser();
          
          // Store masquerading state in localStorage
          const masqueradeState = {
            isMasquerading: true,
            originalUser: originalUser,
            targetUser: targetUser
          };
          
          localStorage.setItem('masquerading', JSON.stringify(masqueradeState));
          
          // Update localStorage user to target user
          localStorage.setItem('user', JSON.stringify(targetUser));
          
          console.log('✅ Masquerading started successfully (localStorage fallback):', targetUser.user_full_name);
          
          return {
            status: 1,
            message: 'Masquerading started successfully',
            originalUser: originalUser,
            targetUser: targetUser
          };
        }
      }
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError);
    }
    
    throw error;
  }
}

export async function stopMasquerading() {
  try {
    // Get masquerading state from localStorage
    const masqueradeData = localStorage.getItem('masquerading');
    if (!masqueradeData) {
      throw new Error('No masquerading state found');
    }
    
    const masqueradeState = JSON.parse(masqueradeData);
    if (!masqueradeState.originalUser) {
      throw new Error('No original user found in masquerading state');
    }
    
    // Clear masquerading state from localStorage
    localStorage.removeItem('masquerading');
    
    // Restore original user in localStorage
    localStorage.setItem('user', JSON.stringify(masqueradeState.originalUser));
    
    console.log('✅ Masquerading stopped successfully (localStorage):', masqueradeState.originalUser.user_full_name);
    
    return {
      status: 1,
      message: 'Masquerading stopped successfully',
      originalUser: masqueradeState.originalUser
    };
  } catch (error) {
    console.error('Stop masquerading failed:', error);
    throw error;
  }
}
